import React from 'react';
import { MessageCircleIcon, UserX, Clock, Calendar, AlertCircle, Globe, Users, Megaphone } from 'lucide-react';
import ScrollReveal from '../ui/ScrollReveal';
const ProblemSection = () => {
  return <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#EDE8F5] relative overflow-hidden" id="about" data-id="element-406">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5" data-id="element-407">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(#3D52A0 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }} data-id="element-408"></div>
      </div>
      <div className="max-w-7xl mx-auto relative" data-id="element-409">
        <ScrollReveal data-id="element-410">
          <div className="flex flex-col items-center mb-16 text-center" data-id="element-411">
            <span className="inline-block px-4 py-1 bg-[#3D52A0]/10 rounded-full text-sm font-medium text-[#3D52A0] mb-6" data-id="element-412">
              The Challenge
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#3D52A0] mb-6 max-w-3xl" data-id="element-413">
              Turn one-time events into lasting connections with the right
              follow-up system.
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#3D52A0] to-[#7091E6] rounded-full mb-6" data-id="element-414"></div>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl" data-id="element-415">
              Churches excel at creating meaningful moments, but struggle to
              maintain momentum after the event ends. The result? Missed
              opportunities to nurture relationships with guests who showed
              interest.
            </p>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mt-4 font-medium" data-id="element-416">
              Shepherd Flow AI bridges this gap, transforming one-time visitors
              into engaged members of your community through strategic,
              automated communication.
            </p>
          </div>
        </ScrollReveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-417">
          <ScrollReveal delay={100} data-id="element-418">
            <div className="bg-white p-8 rounded-xl shadow-lg card-hover border-t-4 border-[#3D52A0] h-full flex flex-col" data-id="element-419">
              <div className="bg-[#7091E6]/20 p-4 rounded-full w-fit mb-6 transform transition-transform duration-300 group-hover:scale-110" data-id="element-420">
                <Calendar className="text-[#3D52A0] h-8 w-8" data-id="element-421" />
              </div>
              <h3 className="text-xl font-semibold text-[#3D52A0] mb-3" data-id="element-422">
                Great at Events—Not at Follow-up
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-423">
                Churches pour energy into hosting events but struggle with
                consistent follow-up.
              </p>
              <div className="mt-auto pt-4 border-t border-gray-100" data-id="element-424">
                <div className="flex items-start text-[#3D52A0]" data-id="element-425">
                  <AlertCircle size={16} className="mr-2 mt-1 flex-shrink-0" data-id="element-426" />
                  <span className="font-medium" data-id="element-427">
                    Automated Sign-Up, Show-Up, and Follow-Up workflows keep
                    every guest engaged long after the event ends.
                  </span>
                </div>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={200} data-id="element-428">
            <div className="bg-white p-8 rounded-xl shadow-lg card-hover border-t-4 border-[#3D52A0] h-full flex flex-col" data-id="element-429">
              <div className="bg-[#7091E6]/20 p-4 rounded-full w-fit mb-6 transform transition-transform duration-300 group-hover:scale-110" data-id="element-430">
                <Users className="text-[#3D52A0] h-8 w-8" data-id="element-431" />
              </div>
              <h3 className="text-xl font-semibold text-[#3D52A0] mb-3" data-id="element-432">
                Pastors, Not Marketers
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-433">
                Church leaders are called to shepherd people, not craft
                marketing campaigns.
              </p>
              <div className="mt-auto pt-4 border-t border-gray-100" data-id="element-434">
                <div className="flex items-start text-[#3D52A0]" data-id="element-435">
                  <AlertCircle size={16} className="mr-2 mt-1 flex-shrink-0" data-id="element-436" />
                  <span className="font-medium" data-id="element-437">
                    Acts like a world-class communication team that writes and
                    designs every message in your church's unique voice.
                  </span>
                </div>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={300} data-id="element-438">
            <div className="bg-white p-8 rounded-xl shadow-lg card-hover border-t-4 border-[#3D52A0] h-full flex flex-col" data-id="element-439">
              <div className="bg-[#7091E6]/20 p-4 rounded-full w-fit mb-6 transform transition-transform duration-300 group-hover:scale-110" data-id="element-440">
                <Clock className="text-[#3D52A0] h-8 w-8" data-id="element-441" />
              </div>
              <h3 className="text-xl font-semibold text-[#3D52A0] mb-3" data-id="element-442">
                Time-Consuming Communication
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-443">
                Creating invites, reminders, and follow-ups consumes hours of
                valuable ministry time.
              </p>
              <div className="mt-auto pt-4 border-t border-gray-100" data-id="element-444">
                <div className="flex items-start text-[#3D52A0]" data-id="element-445">
                  <AlertCircle size={16} className="mr-2 mt-1 flex-shrink-0" data-id="element-446" />
                  <span className="font-medium" data-id="element-447">
                    Generates complete, ready-to-use campaigns in seconds—saving
                    leaders hours of manual work each week.
                  </span>
                </div>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={400} data-id="element-448">
            <div className="bg-white p-8 rounded-xl shadow-lg card-hover border-t-4 border-[#3D52A0] h-full flex flex-col" data-id="element-449">
              <div className="bg-[#7091E6]/20 p-4 rounded-full w-fit mb-6 transform transition-transform duration-300 group-hover:scale-110" data-id="element-450">
                <Globe className="text-[#3D52A0] h-8 w-8" data-id="element-451" />
              </div>
              <h3 className="text-xl font-semibold text-[#3D52A0] mb-3" data-id="element-452">
                Limited Community Reach
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-453">
                Outreach events rarely extend impact beyond those who already
                attend.
              </p>
              <div className="mt-auto pt-4 border-t border-gray-100" data-id="element-454">
                <div className="flex items-start text-[#3D52A0]" data-id="element-455">
                  <AlertCircle size={16} className="mr-2 mt-1 flex-shrink-0" data-id="element-456" />
                  <span className="font-medium" data-id="element-457">
                    Invite Campaigns help connect with communities through
                    personalized messages that draw in new families.
                  </span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>;
};
export default ProblemSection;